import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { auth, getToken, setToken, type StaffUser, ApiError } from "./api";

interface AuthState {
  user: StaffUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      // Session lives in an HttpOnly cookie the browser sends automatically —
      // don't gate on localStorage. Always try /me; 401 means logged out.
      try {
        const me = await auth.me();
        if (!cancelled) setUser(me);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => { cancelled = true; };
  }, []);

  const value: AuthState = {
    user,
    loading,
    async login(email, password) {
      const res = await auth.login(email, password);
      setToken(res.token);
      setUser(res.user);
    },
    async logout() {
      try { await auth.logout(); } catch { /* ignore */ }
      setToken(null);
      setUser(null);
    },
    async refresh() {
      try { setUser(await auth.me()); } catch { /* keep current user */ }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useUser(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useUser must be inside <AuthProvider>");
  return ctx;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--ne-muted)" }}>Loading…</div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Forced password upgrade: an existing password that no longer meets the
  // strength policy blocks the whole staff app until it's changed. The only
  // reachable route is the update screen itself (and sign out).
  if ((user as any).password_update_required === true && location.pathname !== "/password-update") {
    return <Navigate to="/password-update" replace />;
  }
  return <>{children}</>;
}
